import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import StatusChip from '@sophocles/components/StatusChip';
import { MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import {
  useDeleteOrderSaleMutation,
  usePatchOrderSaleMutation,
  useRetrieveOrderSaleQuery,
} from '@sophocles/features/entities/orderSales';
import {
  getOrderSaleCancelDialog,
  getOrderSalePrimaryAction,
} from '@sophocles/features/warehouse/orders/orderSaleActions';
import { formatNumber } from '@sophocles/utils/formatUtils';
import { orderStatusColor } from '@sophocles/views/sales/utils/orderStatus';

import { Metric, SectionLabel, TagChip } from './detailDrawerPrimitives';

function formatCurrency(v, user) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${formatNumber(n, user, MONEY_FORMAT_OPTIONS)}`;
}

/**
 * Right-side detail panel for an OrderSale row. Shows status / scope,
 * indicator tags, line-item summary, and status-aware actions
 * (Edit / View / Bill / Cancel / Delete). Fetches the full order via the
 * odl-app retrieveOrderSale query on open so the items list has denormalized
 * item info.
 *
 * Props:
 *   order     – row data; null while drawer is closed
 *   open      – boolean
 *   onClose   – () => void
 *   onMutated – () => void  called after Cancel/Delete to refresh the grid
 */
export default function OrderDetailDrawer({ order, open, onClose, onMutated }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const [patchOrderSale] = usePatchOrderSaleMutation();
  const [deleteOrderSale] = useDeleteOrderSaleMutation();

  const orderId = order?.id ?? null;

  // Open-gated detail read. RTK Query owns the request lifecycle / cancellation;
  // skip keeps it idle while the drawer is closed or no row is selected.
  const { data: detail, isFetching: loading } = useRetrieveOrderSaleQuery({ id: orderId }, { skip: !open || !orderId });

  // Use the row payload as the immediate baseline; merge detail on top once
  // it lands so headers don't flash empty on open.
  const data = useMemo(() => ({ ...(order || {}), ...(detail || {}) }), [order, detail]);

  const refreshAfterMutation = useCallback(() => {
    onMutated?.();
    onClose?.();
  }, [onMutated, onClose]);

  if (!order) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }

  const {
    id,
    po_num,
    status,
    priority,
    order_time,
    date_req_by,
    customer_detail,
    site_group_detail,
    salesperson_detail,
    order_user_detail,
    order_sale_items = [],
    order_total,
    shipping_cost,
    tracking,
    ship_type,
    has_prepaid,
    external_warehouse_fulfillment,
    should_hold_billing,
    should_notify_customer,
    is_invoiced,
    comments,
  } = data;

  const statusColor = orderStatusColor(status);
  const primaryAction = getOrderSalePrimaryAction(status);
  const cancelDialog = getOrderSaleCancelDialog(status);
  const isCancellable = status === 'New';
  const isDeletable = status === 'Draft';

  const itemCount = order_sale_items.length;
  // Grand total is server-maintained (OrderSale pre_save sums each line's
  // authoritative subtotal); shipping is added on top for the display Total.
  const subtotal = Number(order_total) || 0;
  const totalWithShipping = subtotal + (Number(shipping_cost) || 0);

  const handleEdit = () => {
    navigate(`/sales/orders/${id}`);
    onClose?.();
  };
  const handleDuplicate = () => {
    // Build a stripped duplicate: carry main-area fields, clear
    // fulfillment / identity fields so the form opens as a fresh draft.
    const src = detail ?? order;
    const duplicateItems = (src.order_sale_items ?? []).map(line => ({
      item: line.item,
      item_price: line.item_price,
      qty_order: line.qty_order,
      qty_fill: 0,
      has_radios: line.has_radios,
      radio_billing_type: line.radio_billing_type ?? '',
      prepaid_service: line.prepaid_service ?? null,
      prepaid_cost: line.prepaid_cost ?? 0,
      prepaid_follows_radio: false,
      term_length_prepaid: line.term_length_prepaid ?? 0,
      term_length_trial: line.term_length_trial ?? 0,
      iccids: [],
    }));
    const duplicateFrom = {
      customer: src.customer,
      site_group: src.site_group,
      salesperson: src.salesperson,
      ship_type: src.ship_type ?? null,
      ship_company_name: src.ship_company_name ?? '',
      ship_contact: src.ship_contact ?? '',
      ship_email: src.ship_email ?? '',
      ship_phone: src.ship_phone ?? '',
      ship_address: src.ship_address ?? '',
      ship_city: src.ship_city ?? '',
      ship_state: src.ship_state ?? '',
      ship_zip: src.ship_zip ?? '',
      ship_country: src.ship_country ?? '',
      ship_instructions: src.ship_instructions ?? '',
      bill_contact: src.bill_contact ?? '',
      bill_company_name: src.bill_company_name ?? '',
      bill_email: src.bill_email ?? '',
      bill_phone: src.bill_phone ?? '',
      bill_address: src.bill_address ?? '',
      bill_city: src.bill_city ?? '',
      bill_state: src.bill_state ?? '',
      bill_zip: src.bill_zip ?? '',
      bill_country: src.bill_country ?? '',
      comments: src.comments ?? '',
      order_sale_items: duplicateItems.length > 0 ? duplicateItems : undefined,
    };
    navigate('/sales/orders/new', { state: { duplicateFrom } });
    onClose?.();
  };
  const handleCancel = () => {
    setShowCancelDialog(true);
  };
  const handleConfirmCancel = () => {
    patchOrderSale({ id, body: { status: 'Cancelled' } }).then(() => {
      setShowCancelDialog(false);
      refreshAfterMutation();
    });
  };
  const handleDelete = () => {
    deleteOrderSale({ id }).then(refreshAfterMutation);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          bgcolor: 'var(--card-bg)',
          borderLeft: 1,
          borderColor: 'var(--md-outline-variant)',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{ px: 'var(--page-gap)', py: 2, borderBottom: 1, borderColor: 'var(--md-outline-variant)' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            {priority && (
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
                title={t('warehouse.priority', 'Priority')}
              />
            )}
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              #{id?.toString()?.padStart(5, '0')}
            </Typography>
            {po_num && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                · PO {po_num}
              </Typography>
            )}
            {status && <StatusChip label={status} color={statusColor} sx={{ height: 20, typography: 'caption' }} />}
          </Stack>
          <Typography sx={{ fontSize: 'var(--title-size)', fontWeight: 'var(--title-weight)', lineHeight: 1.2 }}>
            {customer_detail?.name ?? t('warehouse.order', 'Order')}
          </Typography>
          {site_group_detail?.name && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {site_group_detail.name}
              {site_group_detail.description ? ` — ${site_group_detail.description}` : ''}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label={t('actions.close', 'Close')} size="small" sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 'var(--page-gap)', py: 2 }}>
        <SectionLabel accent="info.main">{t('warehouse.summary', 'Summary')}</SectionLabel>
        <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
          <Metric label={t('warehouse.itemsLabel', 'Items')} value={itemCount} accent="info.main" emphasis />
          <Metric label={t('warehouse.subtotal', 'Subtotal')} value={formatCurrency(subtotal, user)} />
          <Metric label={t('general.total', 'Total')} value={formatCurrency(totalWithShipping, user)} />
        </Stack>

        {(has_prepaid ||
          external_warehouse_fulfillment ||
          should_hold_billing ||
          should_notify_customer ||
          is_invoiced) && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 2, flexWrap: 'wrap' }}>
            {has_prepaid && <TagChip color="info.main" label={t('warehouse.hasPrepaid', 'Has Prepaid')} />}
            {external_warehouse_fulfillment && (
              <TagChip color="warning.main" label={t('warehouse.externalWarehouse', 'External Warehouse')} />
            )}
            {should_hold_billing && <TagChip color="error.main" label={t('warehouse.holdBilling', 'Hold Billing')} />}
            {should_notify_customer && (
              <TagChip color="success.main" label={t('warehouse.notifyCustomer', 'Notify Customer')} />
            )}
            {is_invoiced && <TagChip color="text.secondary" label={t('warehouse.invoiced', 'Invoiced')} />}
          </Stack>
        )}
      </Box>

      <Box sx={{ px: 'var(--page-gap)', py: 2, borderTop: 1, borderColor: 'var(--md-outline-variant)' }}>
        <SectionLabel>{t('warehouse.orderInfo', 'Order Info')}</SectionLabel>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <InfoRow
            label={t('warehouse.orderTime', 'Order Time')}
            value={order_time ? new Date(order_time).toLocaleString() : '—'}
          />
          <InfoRow
            label={t('warehouse.dueDate', 'Due Date')}
            value={date_req_by ? new Date(date_req_by).toLocaleDateString() : '—'}
          />
          <InfoRow
            label={t('warehouse.salesPerson', 'Salesperson')}
            value={
              salesperson_detail
                ? `${salesperson_detail.first_name ?? ''} ${salesperson_detail.last_name ?? ''}`.trim() || '—'
                : '—'
            }
          />
          <InfoRow
            label={t('warehouse.inputUser', 'Input User')}
            value={
              order_user_detail
                ? `${order_user_detail.first_name ?? ''} ${order_user_detail.last_name ?? ''}`.trim() || '—'
                : '—'
            }
          />
          {ship_type && <InfoRow label={t('warehouse.shipType', 'Ship Type')} value={ship_type} />}
          {tracking && <InfoRow label={t('warehouse.tracking', 'Tracking')} value={tracking} mono />}
          {shipping_cost != null && Number(shipping_cost) > 0 && (
            <InfoRow label={t('warehouse.shipping', 'Shipping')} value={formatCurrency(shipping_cost, user)} mono />
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 'var(--page-gap)',
          py: 2,
          flex: 1,
          overflowY: 'auto',
          borderTop: 1,
          borderColor: 'var(--md-outline-variant)',
        }}
      >
        <SectionLabel>{t('warehouse.itemsWithCount', 'Items ({{count}})', { count: itemCount })}</SectionLabel>
        {loading && order_sale_items.length === 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={56} />
            ))}
          </Stack>
        ) : order_sale_items.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('warehouse.noItemsOnOrder', 'No items on this order.')}
          </Typography>
        ) : (
          <Stack sx={{ mt: 1 }} divider={<Box sx={{ borderTop: 1, borderColor: 'var(--md-outline-variant)' }} />}>
            {order_sale_items.map((line, idx) => (
              <ItemRow key={line.id ?? idx} line={line} />
            ))}
          </Stack>
        )}
        {comments && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--md-surface-container)', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              {t('general.comments', 'Comments')}
            </Typography>
            <Typography variant="body2">{comments}</Typography>
          </Box>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 'var(--page-gap)',
          py: 1.5,
          borderTop: 1,
          borderColor: 'var(--md-outline-variant)',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            component={Link}
            to={`/sales/orders/${id}/print`}
            variant="text"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          >
            {t('warehouse.printView', 'Print View')}
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
            onClick={handleDuplicate}
            disabled={loading}
          >
            {t('warehouse.duplicate', 'Duplicate')}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          {isDeletable && (
            <Button variant="outlined" color="error" size="small" onClick={handleDelete}>
              {t('actions.delete', 'Delete')}
            </Button>
          )}
          {isCancellable && (
            <Button variant="outlined" color="error" size="small" onClick={handleCancel}>
              {t('warehouse.cancelOrder', 'Cancel Order')}
            </Button>
          )}
          <Button variant="contained" size="small" onClick={handleEdit}>
            {t(primaryAction.i18nKey, primaryAction.label)}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>{t(cancelDialog.titleI18nKey, cancelDialog.title)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t(cancelDialog.bodyI18nKey, 'This will mark the order as Cancelled.')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>{t('warehouse.keepOrder', 'Keep Order')}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmCancel}>
            {t('warehouse.confirmCancelOrder', 'Cancel Order')}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, textAlign: 'right', minWidth: 0 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function ItemRow({ line }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const sku = line.item_detail?.item_part_num;
  const name = line.item_detail?.item_name;
  const description = line.item_detail?.item_description;
  const qty = Number(line.qty_order) || 0;
  const price = Number(line.item_price) || 0;
  const isPrepaid = line.has_radios && line.radio_billing_type === 'Prepaid';
  const prepaidPrice = isPrepaid ? Number(line.prepaid_cost) || 0 : 0;

  const headline = name ? (
    <>
      {sku && (
        <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}>
          {sku}:
        </Box>
      )}
      <Box component="span" sx={{ color: 'text.primary' }}>
        {name}
      </Box>
    </>
  ) : (
    <Box component="span" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
      {line.item ? `#${line.item}` : '—'}
    </Box>
  );

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1.5}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
            {headline}
          </Typography>
          {description && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {qty} × {formatCurrency(price, user)}
          </Typography>
          {/* Authoritative per-line total from the server (calculate_item_subtotal folds
              prepaid via term_length_prepaid); never recomputed as qty × price here. */}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {formatCurrency(line.subtotal, user)}
          </Typography>
        </Stack>
      </Stack>
      {isPrepaid && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          spacing={1.5}
          sx={{ mt: 0.5, pl: 2 }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('warehouse.prepaidTermMo', 'Prepaid term · {{months}} mo', { months: line.term_length_prepaid ?? '?' })}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {qty} × {formatCurrency(prepaidPrice, user)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
